import { InferenceSession, Tensor } from 'onnxruntime-web';
import { FaceDetection, DeviceCapabilities, ProcessingConfig } from '../../types/client';
import { debug } from '../../utils/debug';

export class ONNXFaceDetectionService {
  private session: InferenceSession | null = null;
  private modelPath: string = '';
  private isModelLoaded: boolean = false;
  private loadingProgress: number = 0;

  constructor() {
    this.setupONNXRuntime();
  }

  private setupONNXRuntime(): void {
    // Configure ONNX Runtime for web
    const ort = require('onnxruntime-web');
    
    // Set up execution providers based on device capabilities
    const providers = ['wasm'];
    
    if (this.isWebGLSupported()) {
      providers.unshift('webgl');
      debug.log('✓ WebGL execution provider available');
    }

    // Configure WASM paths (important for production)
    ort.env.wasm.wasmPaths = '/node_modules/onnxruntime-web/dist/';
    ort.env.logLevel = 'warning';
  }

  private isWebGLSupported(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!(
        window.WebGLRenderingContext && 
        canvas.getContext('webgl')
      );
    } catch {
      return false;
    }
  }

  async loadModel(modelPath: string, onProgress?: (progress: number) => void): Promise<void> {
    try {
      this.modelPath = modelPath;
      this.loadingProgress = 0;
      
      debug.log(`🔄 Loading ONNX face detection model from ${modelPath}`);
      
      if (onProgress) onProgress(10);

      // Create session with optimized providers
      const sessionOptions = {
        executionProviders: this.getOptimalProviders(),
        graphOptimizationLevel: 'all' as const,
        enableCpuMemArena: true,
        enableMemPattern: true,
      };

      if (onProgress) onProgress(30);

      this.session = await InferenceSession.create(modelPath, sessionOptions);
      
      if (onProgress) onProgress(100);

      this.isModelLoaded = true;
      this.loadingProgress = 100;

      debug.log('✓ ONNX face detection model loaded successfully');
      debug.log('  - Input names:', this.session.inputNames);
      debug.log('  - Output names:', this.session.outputNames);

    } catch (error) {
      this.isModelLoaded = false;
      this.loadingProgress = 0;
      debug.error('❌ Failed to load ONNX model:', error);
      throw new Error(`Failed to load ONNX model: ${error}`);
    }
  }

  private getOptimalProviders(): string[] {
    const capabilities = this.detectDeviceCapabilities();
    
    if (capabilities.webglSupported && !capabilities.isMobile) {
      return ['webgl', 'wasm'];
    }
    
    return ['wasm'];
  }

  detectDeviceCapabilities(): DeviceCapabilities {
    return {
      isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      availableMemory: (navigator as any).deviceMemory || 4,
      hardwareConcurrency: navigator.hardwareConcurrency || 4,
      webglSupported: this.isWebGLSupported(),
      webgpuSupported: 'gpu' in navigator,
      connection: (navigator as any).connection?.effectiveType
    };
  }

  async detectFaces(imageData: ImageData): Promise<FaceDetection[]> {
    if (!this.isModelLoaded || !this.session) {
      throw new Error('Model not loaded. Call loadModel() first.');
    }

    try {
      debug.log('🔍 Running face detection...');
      const startTime = performance.now();

      // Preprocess image
      const inputTensor = this.preprocessImage(imageData);
      
      // Run inference
      const feeds = { [this.session.inputNames[0]]: inputTensor };
      const results = await this.session.run(feeds);
      
      // Postprocess results
      const faces = this.postprocessResults(results, imageData.width, imageData.height);
      
      const processingTime = performance.now() - startTime;
      debug.log(`✓ Face detection completed in ${processingTime.toFixed(2)}ms`);
      debug.log(`  - Detected ${faces.length} faces`);

      return faces;

    } catch (error) {
      debug.error('❌ Face detection failed:', error);
      throw new Error(`Face detection failed: ${error}`);
    }
  }

  private preprocessImage(imageData: ImageData): Tensor {
    const { width, height, data } = imageData;
    
    // Target size for YOLO model (640x640)
    const targetSize = 640;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    
    // Resize image to target size while maintaining aspect ratio
    const scale = Math.min(targetSize / width, targetSize / height);
    const scaledWidth = Math.round(width * scale);
    const scaledHeight = Math.round(height * scale);
    
    canvas.width = targetSize;
    canvas.height = targetSize;
    
    // Fill with gray background
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, targetSize, targetSize);
    
    // Create ImageData from input
    const sourceCanvas = document.createElement('canvas');
    const sourceCtx = sourceCanvas.getContext('2d')!;
    sourceCanvas.width = width;
    sourceCanvas.height = height;
    sourceCtx.putImageData(imageData, 0, 0);
    
    // Draw resized image centered
    const offsetX = (targetSize - scaledWidth) / 2;
    const offsetY = (targetSize - scaledHeight) / 2;
    ctx.drawImage(sourceCanvas, offsetX, offsetY, scaledWidth, scaledHeight);
    
    // Get processed image data
    const processedImageData = ctx.getImageData(0, 0, targetSize, targetSize);
    const pixelData = processedImageData.data;
    
    // Convert to tensor format [1, 3, 640, 640]
    const tensorData = new Float32Array(3 * targetSize * targetSize);
    
    for (let i = 0; i < targetSize * targetSize; i++) {
      const r = pixelData[i * 4] / 255.0;     // Red
      const g = pixelData[i * 4 + 1] / 255.0; // Green
      const b = pixelData[i * 4 + 2] / 255.0; // Blue
      
      tensorData[i] = r;                              // R channel
      tensorData[targetSize * targetSize + i] = g;   // G channel
      tensorData[2 * targetSize * targetSize + i] = b; // B channel
    }
    
    return new Tensor('float32', tensorData, [1, 3, targetSize, targetSize]);
  }

  private postprocessResults(results: any, originalWidth: number, originalHeight: number): FaceDetection[] {
    const output = results[this.session!.outputNames[0]];
    const predictions = output.data;
    const outputShape = output.dims; // [1, 25200, 6] for YOLOv8
    
    const faces: FaceDetection[] = [];
    const confidenceThreshold = 0.3;
    const targetSize = 640;
    
    // Calculate scale factors to map back to original image
    const scaleX = originalWidth / targetSize;
    const scaleY = originalHeight / targetSize;
    
    const numPredictions = outputShape[1];
    
    for (let i = 0; i < numPredictions; i++) {
      const base = i * 6; // 6 values per prediction [x1, y1, x2, y2, confidence, class]
      
      const confidence = predictions[base + 4];
      
      if (confidence > confidenceThreshold) {
        // Extract bounding box coordinates
        let x1 = predictions[base] * scaleX;
        let y1 = predictions[base + 1] * scaleY;
        let x2 = predictions[base + 2] * scaleX;
        let y2 = predictions[base + 3] * scaleY;
        
        // Ensure coordinates are within image bounds
        x1 = Math.max(0, Math.min(originalWidth, x1));
        y1 = Math.max(0, Math.min(originalHeight, y1));
        x2 = Math.max(0, Math.min(originalWidth, x2));
        y2 = Math.max(0, Math.min(originalHeight, y2));
        
        // Validate bounding box
        if (x2 > x1 && y2 > y1) {
          faces.push({
            bbox: [x1, y1, x2, y2],
            confidence: confidence
          });
        }
      }
    }
    
    // Apply Non-Maximum Suppression (simple version)
    return this.applyNMS(faces, 0.5);
  }

  private applyNMS(faces: FaceDetection[], iouThreshold: number): FaceDetection[] {
    if (faces.length === 0) return [];
    
    // Sort by confidence (descending)
    faces.sort((a, b) => b.confidence - a.confidence);
    
    const keep: FaceDetection[] = [];
    const suppress = new Set<number>();
    
    for (let i = 0; i < faces.length; i++) {
      if (suppress.has(i)) continue;
      
      keep.push(faces[i]);
      
      for (let j = i + 1; j < faces.length; j++) {
        if (suppress.has(j)) continue;
        
        const iou = this.calculateIoU(faces[i].bbox, faces[j].bbox);
        if (iou > iouThreshold) {
          suppress.add(j);
        }
      }
    }
    
    return keep;
  }

  private calculateIoU(boxA: [number, number, number, number], boxB: [number, number, number, number]): number {
    const [x1A, y1A, x2A, y2A] = boxA;
    const [x1B, y1B, x2B, y2B] = boxB;
    
    // Calculate intersection area
    const intersectionX1 = Math.max(x1A, x1B);
    const intersectionY1 = Math.max(y1A, y1B);
    const intersectionX2 = Math.min(x2A, x2B);
    const intersectionY2 = Math.min(y2A, y2B);
    
    if (intersectionX2 <= intersectionX1 || intersectionY2 <= intersectionY1) {
      return 0;
    }
    
    const intersectionArea = (intersectionX2 - intersectionX1) * (intersectionY2 - intersectionY1);
    
    // Calculate union area
    const areaA = (x2A - x1A) * (y2A - y1A);
    const areaB = (x2B - x1B) * (y2B - y1B);
    const unionArea = areaA + areaB - intersectionArea;
    
    return unionArea > 0 ? intersectionArea / unionArea : 0;
  }

  isLoaded(): boolean {
    return this.isModelLoaded;
  }

  getLoadingProgress(): number {
    return this.loadingProgress;
  }

  dispose(): void {
    if (this.session) {
      this.session.release();
      this.session = null;
    }
    this.isModelLoaded = false;
    this.loadingProgress = 0;
    debug.log('🧹 ONNX face detection service disposed');
  }
}